import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fundCampaignFromBalance } from "../../api/campaigns";
import { getBalance } from "../../api/balance";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "../../context/TranslationContext";
import { formatCampaignMoney } from "../../utils/campaignCardUtils";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import InlineMessage from "../ds/InlineMessage";
import Modal from "../ds/Modal";
import { APP_FILLED, FilterChip } from "./shared";

const QUICK_AMOUNTS = [500, 1000, 2000];

const money = (value) => `${formatCampaignMoney(value)} ₾`;

/**
 * Figma O4: moves money from the owner's Mintly balance into a campaign. Paying by
 * card stays on the payment page, one button away.
 */
const TopUpBudgetModal = ({ open, onClose, campaign, setToast, onFunded, isDark }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [amount, setAmount] = useState("500");
  const [available, setAvailable] = useState(null);
  const [saving, setSaving] = useState(false);
  const userId = user?.id ?? user?._id;

  useEffect(() => {
    if (!open || !userId) return;
    setAmount("500");
    getBalance(userId)
      .then((balance) => setAvailable(Number(balance?.amount) || 0))
      .catch(() => setAvailable(0));
  }, [open, userId]);

  const value = Number(amount) || 0;
  const short = available !== null && value > available;
  const left = Math.max((campaign?.budget || 0) - (campaign?.spent || 0), 0);

  const fund = async () => {
    setSaving(true);
    try {
      const mintSpaceId = campaign?.mintSpace?._id || campaign?.mintSpace || undefined;
      const res = await fundCampaignFromBalance(campaign._id, value, mintSpaceId);
      if (res.success) {
        window.dispatchEvent(new CustomEvent("balance-updated"));
        setToast?.(t("campaigns.fundedFromWalletMessage", { amount: value.toFixed(2) }));
        onFunded?.(value, res);
        onClose();
      }
    } catch (error) {
      setToast?.(error.response?.data?.message || error.message || t("toasts.paymentFailedTryAgain"));
    } finally {
      setSaving(false);
    }
  };

  const payByCard = () => {
    onClose();
    navigate(`/payment?campaign_id=${campaign._id}&campaign_name=${encodeURIComponent(campaign.campaignName)}`);
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      title={t("campaignFlow.topUpTitle")}
      description={t("campaignFlow.topUpLeft", { name: campaign?.campaignName, amount: money(left) })}
      closeLabel={t("common.close")}
      isDark={isDark}
    >
      <label className="flex flex-col items-center gap-space-8 rounded-[22px] bg-bg-inset p-space-20">
        <span className="text-caps-label uppercase text-label-tertiary">{t("campaignFlow.amount")}</span>
        <span className="flex items-baseline gap-space-8 text-large-title text-label-primary">
          <input
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value.replace(/[^0-9.]/g, ""))}
            aria-label={t("campaignFlow.amount")}
            size={Math.max(amount.length, 1)}
            className="min-w-[1ch] bg-transparent text-right outline-none"
          />
          ₾
        </span>
      </label>
      <div className="flex flex-wrap justify-center gap-space-8">
        {QUICK_AMOUNTS.map((quick) => (
          <FilterChip key={quick} selected={value === quick} onClick={() => setAmount(String(quick))}>
            {quick.toLocaleString()} ₾
          </FilterChip>
        ))}
      </div>
      <div className="flex items-center gap-space-12 rounded-[22px] border border-separator-default p-space-16">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-[12px] bg-fill-tertiary text-label-primary">
          <Icon name="wallet" size={18} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="truncate text-subheadline-semibold text-label-primary">{t("campaignFlow.mintlyBalance")}</p>
          <p className={`truncate text-footnote ${short ? "text-status-danger" : "text-label-secondary"}`}>
            {t("campaignFlow.available", { amount: money(available ?? 0) })}
          </p>
        </div>
      </div>
      {short && (
        <InlineMessage tone="warning" icon="clock" title={t("campaignFlow.notEnoughTitle")}>
          {t("campaignFlow.notEnoughBody", { amount: money(value - available) })}
        </InlineMessage>
      )}
      <div className="flex flex-col-reverse gap-space-8 min-[600px]:flex-row min-[600px]:justify-end">
        <Button variant="gray" icon={<Icon name="plus" size={20} />} disabled={saving} onClick={payByCard}>
          {t("campaignFlow.addFunds")}
        </Button>
        <Button
          className={value > 0 && !short && !saving ? APP_FILLED : ""}
          disabled={value <= 0 || short}
          loading={saving}
          onClick={fund}
        >
          {t("campaignFlow.topUpAmount", { amount: `${value.toLocaleString()} ₾` })}
        </Button>
      </div>
    </Modal>
  );
};

export default TopUpBudgetModal;
